import React, { Suspense } from 'react';
import { RouteComponentProps } from '@reach/router';

import { Helmet } from 'react-helmet-async';
import { RequirePermission } from '#core/Permissions';
import ErrorBoundary from '#coreComponents/ErrorBoundary';
import NotFound from '#core/NotFound';

import { EventTrackingPage } from '#core/Tracking';
import { PageConfig } from '../../config/products';
import Loading from '#coreComponents/Loading';
import EventTrackingPageView from '#core/Tracking/components/EventTrackingPageView';
// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useAuth } from '#core/Auth';
import AccessDeniedErrorScreen from '#coreComponents/ErrorBoundary/AccessDeniedErrorScreen';

export type PrivateRouteComponentProps = {
  pageConfig: PageConfig;
  auth?: AuthProp;
  upsellComponent?: React.FC<{}>;
} & RouteComponentProps;

export type PrivateRouteLayoutProps = {
  pageConfig: PageConfig;
};

type AuthProp = {
  action: string;
};
interface PrivateRouteProps extends RouteComponentProps {
  layout?: React.FC<PrivateRouteLayoutProps>;
  layoutConfig?: DeepPartial<PageConfig>;
  component: React.FC<PrivateRouteComponentProps>;
  upsellComponent?: React.FC<{}>;
  path?: string;
  default?: boolean;
  auth?: AuthProp;
  errorBoundaryId: string;
  trackingPageGroup: string;
  trackingPageName: string;
  pageTitle: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  layout: Layout = React.Fragment,
  layoutConfig,
  component: Component,
  path,
  auth,
  errorBoundaryId,
  trackingPageGroup,
  trackingPageName,
  upsellComponent,
  pageTitle,
  ...routeProps
}) => {
  const { isAuthenticated } = useAuth(); // JME2

  if (isAuthenticated) {
    return (
      <EventTrackingPage pageGroup={trackingPageGroup} page={trackingPageName}>
        <EventTrackingPageView />
        <ErrorBoundary id={errorBoundaryId} showDetailedErrorScreen>
          <Layout pageConfig={layoutConfig as PageConfig}>
            <Helmet title={pageTitle} />
            <Suspense fallback={<Loading showLogo={true} />}>
              {auth ? (
                <RequirePermission
                  resource="the:datapoints::"
                  action="datapoints:userAccess"
                  DeniedComponent={
                    upsellComponent ||
                    (() => (
                      <AccessDeniedErrorScreen text="You do not have access to the DataPoints platform." />
                    ))
                  }
                >
                  <Component
                    path={path}
                    auth={auth}
                    upsellComponent={upsellComponent}
                    pageConfig={layoutConfig as PageConfig}
                    {...routeProps}
                  />
                </RequirePermission>
              ) : (
                <Component
                  path={path}
                  pageConfig={layoutConfig as PageConfig}
                  {...routeProps}
                />
              )}
            </Suspense>
          </Layout>
        </ErrorBoundary>
      </EventTrackingPage>
    );
  }

  return <NotFound />;
};

export default PrivateRoute;
